// External libraries
import { configureStore } from '@reduxjs/toolkit';
import { persistStore } from 'redux-persist';
import persistCombineReducers from 'redux-persist/es/persistCombineReducers';
import storage from 'redux-persist/lib/storage/session';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { Action } from 'redux';
import * as Sentry from '@sentry/react';

// Helpers
import { deepRedact } from './helpers/utils';

// RTK slices
import { baseApi } from './api/api';
import { authApi } from './features/auth/authApiSlice';

// Middleware
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import notificationMiddleware from './components/notifications/notificationMiddleware';

// Reducers
import authReducer from './features/auth/authReducer';
import publisherReducer from './features/Browse/publisherReducer';
import publishersReducer from './features/Browse/publishersReducer';
import loadingReducer from './reducers/loadingReducer';
import notificationsReducer from './components/notifications/notificationsReducer';

const PERSIST_KEY = 'root';

// SEC-778 / GC-2009: redux-persist used to write this app's persisted state
// (including the JWT) to localStorage under `persist:<key>`. Now that the
// storage engine below is sessionStorage, that old localStorage entry is
// stale and never cleaned up on its own, so every existing browser profile
// keeps a valid JWT sitting in localStorage until it expires naturally.
// Purge it once, synchronously, before persistStore() below creates the
// persistor and kicks off rehydration.
try {
    window.localStorage.removeItem(`persist:${PERSIST_KEY}`);
} catch {
    // localStorage can throw (e.g. some private-browsing modes); never let
    // this crash app startup.
}

const persistConfig = {
    key: PERSIST_KEY,
    storage,
    whitelist: ['auth', 'publisher', 'publishers'], // selects ONLY reducers to persist
};

const persistedReducer = persistCombineReducers(persistConfig, {
    auth: authReducer,
    notifications: notificationsReducer,
    publisher: publisherReducer,
    publishers: publishersReducer,
    loading: loadingReducer,
    [authApi.reducerPath]: authApi.reducer,
    [baseApi.reducerPath]: baseApi.reducer,
});

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    actionTransformer(action: Action<string>) {
        return deepRedact(action, ['jwt', 'email']);
    },
    stateTransformer(state: RootState) {
        return deepRedact(state, ['jwt', 'email']);
    },
});

export const store = configureStore({
    reducer: persistedReducer,
    // @ts-ignore
    enhancers: getDefaultEnhancers => {
        return getDefaultEnhancers().concat(sentryReduxEnhancer);
    },
    // @ts-ignore
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: false,
        })
            .concat(baseApi.middleware)
            .concat(authApi.middleware)
            .concat(notificationMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const persistor = persistStore(store);
